import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { TrackableButton } from "@/components/TrackableButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Calendar, RefreshCcw, ChevronLeft, ChevronRight, FileDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { objectsToCsv, saveCsvToDevice } from "@/utils/csvExport";
import { Capacitor } from "@capacitor/core";
import { toast } from "@/hooks/use-toast";
import { formatInDeviceTimezone } from "@/utils/dateUtils";

interface ActivityLog {
  id: string;
  user_id: string;
  action: string;
  metadata: any;
  created_at: string;
  user_email?: string;
}

const ITEMS_PER_PAGE = 10;

const UserActivityPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [selectedAction, setSelectedAction] = useState<string>("all");
  const [users, setUsers] = useState<{ id: string; email: string }[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [exporting, setExporting] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionRef = useRef<any>(null);

  useEffect(() => {
    fetchUsers();
    fetchActionTypes();
    
    // Set up periodic refresh every 15 seconds for activity logs
    intervalRef.current = setInterval(() => {
      fetchLogs(true);
    }, 15000);

    // Set up real-time subscription for activity_logs table changes
    const setupRealtimeSubscription = async () => {
      try {
        subscriptionRef.current = supabase
          .channel('activity-logs-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'activity_logs'
            },
            (payload) => {
              
              // Debounce rapid changes by refreshing after a short delay
              setTimeout(() => {
                fetchLogs(true);
              }, 1000);
            }
          )
          .subscribe();
      } catch (error) {
        
      }
    };

    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, selectedUser, selectedAction, dateFrom, dateTo]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      
    }
  };

  const fetchActionTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('action')
        .limit(100);

      if (error) throw error;
      
      // Get unique action types
      const uniqueActions = Array.from(new Set(data?.map(log => log.action) || []));
      setActions(uniqueActions);
    } catch (error) {
      
    }
  };

  const fetchLogs = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Start building the query
      let query = supabase
        .from('activity_logs')
        .select('*', { count: 'exact' });
      
      // Apply filters
      if (selectedUser !== "all") {
        query = query.eq('user_id', selectedUser);
      }
      
      if (selectedAction !== "all") {
        query = query.eq('action', selectedAction);
      }
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        query = query.gte('created_at', fromDate.toISOString());
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', toDate.toISOString());
      }
      
      // Add pagination
      const from = (page - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      
      query = query
        .order('created_at', { ascending: false })
        .range(from, to);
      
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      // Get user emails in a separate query since join doesn't work
      if (data && data.length > 0) {
        const userIds = Array.from(new Set(data.map(log => log.user_id)));
        
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIds);
          
        if (!usersError && usersData) {
          // Create a map of user IDs to emails
          const userMap = new Map();
          usersData.forEach(user => userMap.set(user.id, user.email));
          
          // Merge user emails into logs
          const logsWithEmails = data.map(log => ({
            ...log,
            user_email: userMap.get(log.user_id) || 'Unknown User'
          }));
          
          setLogs(logsWithEmails);
        } else {
          // If we can't get user emails, just use the logs as is
          setLogs(data);
        }
      } else {
        setLogs([]);
      }
      
      if (count !== null) {
        setTotalCount(count);
        setTotalPages(Math.ceil(count / ITEMS_PER_PAGE));
      }
      
      setLastUpdated(new Date());
    } catch (error) {
      
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const clearFilters = () => {
    setSelectedUser("all");
    setSelectedAction("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setPage(1);
  };

  const handleManualRefresh = () => {
    fetchLogs(true);
  };

  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatActionName = (action: string) => {
    switch (action) {
      case 'login':
        return 'Giriş';
      case 'logout':
        return 'Çıkış';
      case 'create_project':
        return 'Proje Oluşturma';
      case 'update_project':
        return 'Proje Güncelleme';
      case 'delete_project':
        return 'Proje Silme';
      case 'create_entry':
        return 'Kayıt Oluşturma';
      case 'update_entry':
        return 'Kayıt Güncelleme';
      case 'delete_entry':
        return 'Kayıt Silme';
      case 'send_push_notification':
        return 'Bildirim Gönderme';
      case 'user_navigation':
        return 'Sayfa Gezinme';
      case 'user_click':
        return 'Tıklama';
      case 'user_form_interaction':
        return 'Form Etkileşimi';
      case 'user_scroll':
        return 'Scroll';
      case 'user_focus':
        return 'Odaklanma/Çıkış';
      case 'user_idle':
        return 'Boşta Kalma';
      case 'user_custom':
        return 'Özel Eylem';
      case 'feature_used':
        return 'Özellik Kullanımı';
      case 'export_data':
        return 'Veri İndirme';
      case 'upload_file':
        return 'Dosya Yükleme';
      case 'app_error':
        return 'Uygulama Hatası';
      default:
        return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const formatDateTime = (dateString: string) => {
    return formatInDeviceTimezone(new Date(dateString), 'dd MMM yyyy HH:mm', { locale: tr });
  };

  const getMetadataString = (metadata: any) => {
    if (!metadata) return '';
    
    try {
      // Handle user interaction activities
      if (metadata.type === 'navigation') {
        return `Sayfa: ${metadata.page || metadata.data?.page || 'bilinmeyen'}`;
      }
      
      if (metadata.type === 'click') {
        return `Hedef: ${metadata.target || metadata.data?.text?.slice(0, 30) || 'bilinmeyen'}`;
      }
      
      if (metadata.type === 'form_interaction') {
        return `Form: ${metadata.data?.form || 'bilinmeyen'}, Alan: ${metadata.data?.name || metadata.data?.id || 'bilinmeyen'}`;
      }
      
      if (metadata.type === 'scroll') {
        return `Sayfa: ${metadata.data?.page || metadata.page || 'bilinmeyen'}, Scroll: %${metadata.data?.scrollPercentage || 0}`;
      }
      
      if (metadata.type === 'focus') {
        return `Sayfa: ${metadata.data?.page || metadata.page || 'bilinmeyen'}, Görünür: ${metadata.data?.visible ? 'Evet' : 'Hayır'}`;
      }
      
      if (metadata.type === 'idle') {
        return `Boşta kalma süresi: ${Math.round((metadata.data?.idleDuration || 0) / 60000)} dakika`;
      }
      
      // Handle traditional activities
      if (metadata.project_id) {
        return `Proje ID: ${metadata.project_id}`;
      }
      
      if (metadata.recipient_count) {
        return `${metadata.recipient_count} alıcı, Tür: ${metadata.notification_type || 'genel'}`;
      }
      
      if (metadata.feature) {
        return `Özellik: ${metadata.feature}`;
      }
      
      if (metadata.error) {
        return `Hata: ${metadata.error.slice(0, 50)}...`;
      }
      
      if (metadata.fileName) {
        return `Dosya: ${metadata.fileName} (${Math.round((metadata.fileSize || 0) / 1024)} KB)`;
      }
      
      // Generic handling for other metadata
      const importantKeys = ['page', 'target', 'changes', 'type', 'description'];
      const relevantEntries = Object.entries(metadata).filter(([key]) => 
        importantKeys.includes(key) || !key.startsWith('user') && !key.startsWith('timestamp')
      );
      
      if (relevantEntries.length === 0) {
        return 'Detay yok';
      }
      
      return relevantEntries
        .slice(0, 3) // Show max 3 entries
        .map(([key, value]) => `${key}: ${String(value).slice(0, 30)}`)
        .join(', ');
    } catch (e) {
      return JSON.stringify(metadata).slice(0, 50) + '...';
    }
  };

  // Function to export logs as Excel/CSV
  const handleExportLogs = async () => {
    if (loading || exporting) return;
    
    try {
      setExporting(true);
      toast({
        title: "Dışa Aktarma Başladı",
        description: "Aktivite logları hazırlanıyor...",
        duration: 3000,
      });
      
      // Fetch all logs (without pagination) applying the current filters
      let query = supabase
        .from('activity_logs')
        .select('*');
      
      // Apply filters
      if (selectedUser !== "all") {
        query = query.eq('user_id', selectedUser);
      }
      
      if (selectedAction !== "all") {
        query = query.eq('action', selectedAction);
      }
      
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        query = query.gte('created_at', fromDate.toISOString());
      }
      
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', toDate.toISOString());
      }
      
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Get user emails in a separate query
      let logsWithEmails: any[] = [];
      
      if (data && data.length > 0) {
        const userIds = Array.from(new Set(data.map(log => log.user_id)));
        
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIds);
          
        if (!usersError && usersData) {
          // Create a map of user IDs to emails
          const userMap = new Map();
          usersData.forEach(user => userMap.set(user.id, user.email));
          
          // Merge user emails into logs
          logsWithEmails = data.map(log => ({
            ...log,
            user_email: userMap.get(log.user_id) || 'Unknown User'
          }));
        } else {
          logsWithEmails = data;
        }
      }
      
      if (logsWithEmails.length === 0) {
        toast({
          title: "Dışa Aktarma Tamamlandı",
          description: "Dışa aktarılacak log bulunamadı.",
          duration: 3000,
        });
        return;
      }
      
      // Format logs for CSV
      const formattedLogs = logsWithEmails.map(log => ({
        tarih: format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss'),
        kullanici: log.user_email,
        islem: formatActionName(log.action),
        detaylar: getMetadataString(log.metadata)
      }));
      
      // Create CSV file
      const csvData = objectsToCsv(formattedLogs, [
        { key: 'tarih', label: 'Tarih' },
        { key: 'kullanici', label: 'Kullanıcı' },
        { key: 'islem', label: 'İşlem' },
        { key: 'detaylar', label: 'Detaylar' }
      ]);
      
      // Save and share the file
      const date = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      const filename = `santiyex-aktivite-loglari-${date}.csv`;
      
      const result = await saveCsvToDevice({
        filename,
        csvData
      });
      
      if (result.success) {
        toast({
          title: "Dışa Aktarma Başarılı",
          description: "Aktivite logları başarıyla dışa aktarıldı.",
          duration: 3000,
        });
      } else {
        throw new Error(result.error || "Dışa aktarma başarısız oldu.");
      }
    } catch (error) {
      
      toast({
        title: "Dışa Aktarma Hatası",
        description: "Loglar dışa aktarılırken bir hata oluştu.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <CardTitle>Kullanıcı Aktiviteleri</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Son güncelleme: {formatLastUpdated()}
            </p>
          </div>
          <div className="flex space-x-2">
            <TrackableButton 
              variant="outline" 
              size="sm" 
              onClick={handleExportLogs}
              disabled={loading || exporting}
              trackingData={{
                feature: 'admin_panel',
                action: 'export_activity_logs',
                category: 'data_export',
                context: { 
                  totalLogs: totalCount,
                  hasFilters: selectedUser !== 'all' || selectedAction !== 'all' || dateFrom || dateTo
                }
              }}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              Excel Olarak İndir
            </TrackableButton>
            <TrackableButton 
              variant="outline" 
              size="sm" 
              onClick={handleManualRefresh}
              disabled={loading || refreshing}
              trackingData={{
                feature: 'admin_panel',
                action: 'refresh_activity_logs',
                category: 'data_refresh'
              }}
            >
              {loading || refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCcw className="h-4 w-4" />
              )}
            </TrackableButton>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Filtreler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Kullanıcı</label>
                  <Select 
                    value={selectedUser} 
                    onValueChange={setSelectedUser}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kullanıcı seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">İşlem Tipi</label>
                  <Select 
                    value={selectedAction} 
                    onValueChange={setSelectedAction}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="İşlem seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm İşlemler</SelectItem>
                      {actions.map(action => (
                        <SelectItem key={action} value={action}>
                          {formatActionName(action)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Başlangıç Tarihi</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, 'PPP', { locale: tr }) : "Tarih seçin"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Bitiş Tarihi</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, 'PPP', { locale: tr }) : "Tarih seçin"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={clearFilters}
                  >
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Filtreleri Temizle
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Aktivite Logları</CardTitle>
                <div className="text-sm text-muted-foreground">
                  Toplam: {totalCount} kayıt
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center items-center h-60">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  Kayıt bulunamadı
                </div>
              ) : (
                <>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Zaman</TableHead>
                          <TableHead>Kullanıcı</TableHead>
                          <TableHead>İşlem</TableHead>
                          <TableHead>Detaylar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map(log => (
                          <TableRow key={log.id}>
                            <TableCell className="whitespace-nowrap">
                              {formatDateTime(log.created_at)}
                            </TableCell>
                            <TableCell>{log.user_email}</TableCell>
                            <TableCell>{formatActionName(log.action)}</TableCell>
                            <TableCell className="text-xs max-w-xs truncate">
                              {getMetadataString(log.metadata)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Sayfa {page} / {totalPages}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserActivityPanel; 